import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { PublicShell } from './PublicShell';

/** The snippets have to match what the API actually accepts. The mockup sent
 *  `"to": "user@example.com"`, but SendEmailRequest declares
 *  `to: list[EmailStr]` with min_length=1 -- a bare string is a 422. A landing
 *  page that shows a request the server rejects is worse than no snippet. */
const SNIPPETS = [
  {
    id: 'curl',
    label: 'curl',
    lines: [
      { t: 'mist', s: '# send your first email' },
      { t: 'plain', s: 'curl -X POST https://api.mailgiri.com/v1/emails \\' },
      { t: 'mixed', pre: '  -H "Authorization: Bearer ', mid: 'mg_live_••••••••', midT: 'amber', post: '" \\' },
      { t: 'plain', s: '  -H "Content-Type: application/json" \\' },
      { t: 'plain', s: "  -d '{" },
      { t: 'mixed', pre: '    "from": "', mid: 'hello@yourapp.com', midT: 'wire', post: '",' },
      { t: 'mixed', pre: '    "to": ["', mid: 'user@example.com', midT: 'wire', post: '"],' },
      { t: 'mixed', pre: '    "subject": "', mid: 'Welcome!', midT: 'wire', post: '",' },
      { t: 'mixed', pre: '    "html": "', mid: "<p>Glad you're here.</p>", midT: 'wire', post: '"' },
      { t: 'plain', s: "  }'" },
      { t: 'plain', s: '' },
      { t: 'mist', s: '# response' },
      { t: 'amber', s: '202 Accepted' },
      { t: 'plain', s: '{ "id": "msg_9f2a1c", "status": "queued" }' },
    ],
  },
  {
    id: 'node',
    label: 'Node',
    lines: [
      { t: 'mist', s: '// send your first email' },
      { t: 'plain', s: "const res = await fetch('https://api.mailgiri.com/v1/emails', {" },
      { t: 'plain', s: "  method: 'POST'," },
      { t: 'plain', s: '  headers: {' },
      { t: 'mixed', pre: "    Authorization: 'Bearer ", mid: 'mg_live_••••••••', midT: 'amber', post: "'," },
      { t: 'plain', s: "    'Content-Type': 'application/json'," },
      { t: 'plain', s: '  },' },
      { t: 'plain', s: '  body: JSON.stringify({' },
      { t: 'mixed', pre: "    from: '", mid: 'hello@yourapp.com', midT: 'wire', post: "'," },
      { t: 'mixed', pre: "    to: ['", mid: 'user@example.com', midT: 'wire', post: "']," },
      { t: 'mixed', pre: "    subject: '", mid: 'Welcome!', midT: 'wire', post: "'," },
      { t: 'mixed', pre: "    html: '", mid: "<p>Glad you're here.</p>", midT: 'wire', post: "'," },
      { t: 'plain', s: '  }),' },
      { t: 'plain', s: '});' },
      { t: 'plain', s: '' },
      { t: 'mist', s: '// { id: "msg_9f2a1c", status: "queued" }' },
      { t: 'plain', s: 'const { id } = await res.json();' },
    ],
  },
  {
    id: 'python',
    label: 'Python',
    lines: [
      { t: 'mist', s: '# send your first email' },
      { t: 'plain', s: 'import httpx' },
      { t: 'plain', s: '' },
      { t: 'plain', s: 'res = httpx.post(' },
      { t: 'plain', s: '    "https://api.mailgiri.com/v1/emails",' },
      { t: 'mixed', pre: '    headers={"Authorization": "Bearer ', mid: 'mg_live_••••••••', midT: 'amber', post: '"},' },
      { t: 'plain', s: '    json={' },
      { t: 'mixed', pre: '        "from": "', mid: 'hello@yourapp.com', midT: 'wire', post: '",' },
      { t: 'mixed', pre: '        "to": ["', mid: 'user@example.com', midT: 'wire', post: '"],' },
      { t: 'mixed', pre: '        "subject": "', mid: 'Welcome!', midT: 'wire', post: '",' },
      { t: 'mixed', pre: '        "html": "', mid: "<p>Glad you're here.</p>", midT: 'wire', post: '",' },
      { t: 'plain', s: '    },' },
      { t: 'plain', s: ')' },
      { t: 'plain', s: '' },
      { t: 'mist', s: '# {"id": "msg_9f2a1c", "status": "queued"}' },
      { t: 'plain', s: 'print(res.json())' },
    ],
  },
] as const;

type Line = (typeof SNIPPETS)[number]['lines'][number];

function cls(t: string) {
  return t === 'mist' ? 'c-mist' : t === 'amber' ? 'c-amber' : t === 'wire' ? 'c-wire' : undefined;
}

function CodeLine({ line }: { line: Line }) {
  if (line.t === 'mixed') {
    return (
      <>
        {line.pre}
        <span className={cls(line.midT)}>{line.mid}</span>
        {line.post}
        {'\n'}
      </>
    );
  }
  const className = cls(line.t);
  return className ? (
    <>
      <span className={className}>{line.s}</span>
      {'\n'}
    </>
  ) : (
    <>
      {line.s}
      {'\n'}
    </>
  );
}

/** The design draws three tabs but wires none of them. Tabs that look
 *  interactive and do nothing are worse than no tabs, so these are real: a
 *  proper tablist with arrow-key navigation, which is what a keyboard user
 *  expects once something is announced as a tab. */
function CodeCard() {
  const [active, setActive] = useState(0);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  function onKeyDown(event: React.KeyboardEvent) {
    const delta = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    if (!delta) return;
    event.preventDefault();
    const next = (active + delta + SNIPPETS.length) % SNIPPETS.length;
    setActive(next);
    tabRefs.current[next]?.focus();
  }

  return (
    <div className="code-card">
      <div className="code-tabs" role="tablist" aria-label="Code examples" onKeyDown={onKeyDown}>
        {SNIPPETS.map((snippet, index) => (
          <button
            key={snippet.id}
            ref={(node) => {
              tabRefs.current[index] = node;
            }}
            type="button"
            role="tab"
            id={`tab-${snippet.id}`}
            aria-selected={index === active}
            aria-controls={`panel-${snippet.id}`}
            // Only the selected tab is in the tab order; arrows move between
            // them. Tabbing through every tab is the wrong model for a tablist.
            tabIndex={index === active ? 0 : -1}
            className={index === active ? 'code-tab active' : 'code-tab'}
            onClick={() => setActive(index)}
          >
            {snippet.label}
          </button>
        ))}
      </div>
      <pre
        className="code-body"
        role="tabpanel"
        id={`panel-${SNIPPETS[active].id}`}
        aria-labelledby={`tab-${SNIPPETS[active].id}`}
        tabIndex={0}
      >
        <code>
          {SNIPPETS[active].lines.map((line, i) => (
            <CodeLine key={i} line={line} />
          ))}
        </code>
      </pre>
    </div>
  );
}

const FEATURES = [
  {
    label: 'Send it your way',
    title: 'REST API + SMTP',
    body: 'Call a simple API or point your existing SMTP config at us — same suppression rules and tracking either way.',
  },
  {
    label: 'Serious deliverability',
    title: 'Built to land in the inbox',
    body: 'DKIM, SPF, DMARC, and dedicated IP pools — the plumbing that gets you out of the spam folder, handled for you.',
  },
  {
    label: 'See what happened',
    title: 'Events & webhooks',
    body: 'Bounces, complaints, opens, and clicks pushed to your systems the moment they happen — not buried in a CSV.',
  },
];

export function Landing() {
  // The third node lights up shortly after load: the diagram reads as a journey
  // rather than a static row. Purely decorative, so it is skipped outright when
  // reduced motion is asked for -- and the node simply starts lit instead.
  const reduceMotion =
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const [inboxLit, setInboxLit] = useState(Boolean(reduceMotion));

  useEffect(() => {
    if (reduceMotion) return;
    const timer = setTimeout(() => setInboxLit(true), 600);
    return () => clearTimeout(timer);
  }, [reduceMotion]);

  return (
    <PublicShell>
      <section className="hero">
        <div>
          <span className="eyebrow">Email infrastructure</span>
          <h1>
            Send email your&nbsp;app
            <br />
            can <em>depend</em> on.
          </h1>
          <p className="sub">
            A REST API and SMTP relay for transactional and marketing email — with delivery
            tracking, suppression handling, and webhooks done right from day one.
          </p>

          <div className="cta-row">
            <Link className="btn-primary" to="/signup">
              Get started free
            </Link>
            <Link className="link-secondary" to="/pricing">
              See pricing
            </Link>
          </div>

          {/* Decorative diagram: the hero copy above already states what the
              product does, so this is not announced as a list of three words. */}
          <div className="route" aria-hidden="true">
            <div className="route-node active">
              <div className="route-dot" />
              your app
            </div>
            <div className="route-line" />
            <div className="route-node active">
              <div className="route-dot" />
              mailgiri
            </div>
            <div className="route-line" />
            <div className={inboxLit ? 'route-node active' : 'route-node'}>
              <div className="route-dot" />
              inbox
            </div>
          </div>
        </div>

        <CodeCard />
      </section>

      <section className="features">
        {FEATURES.map((feature) => (
          <div className="feature" key={feature.title}>
            <div className="feature-label">{feature.label}</div>
            <h3>{feature.title}</h3>
            <p>{feature.body}</p>
          </div>
        ))}
      </section>
    </PublicShell>
  );
}
